import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Userfront from '../auth/userfrontCompat';
import api from './../api/axios';

const SubscriptionRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const skipPaths = ['/login', '/subscription', '/confirmation', '/set-password', '/reset-password'];
        if (skipPaths.includes(location.pathname)) return;

        const email = Userfront.user?.email;
        if (!email) return;

        const userRes = await api.get(`/user/user`);
        const user = userRes.data.user;
        if (!user) return;

        // Redirect if a legacy invited user has not set a password yet.
        if (user.hasPasswordSet === false) {
          navigate('/set-password', { replace: true });
          return;
        }

        // Auth0 is the source of truth for email verification. The ID-token
        // compatibility value can be stale immediately after a verification link
        // is consumed, so always prefer the backend's live /userinfo check.
        let emailVerified = Userfront.user?.isConfirmed !== false;
        try {
          const verificationRes = await api.get('/auth/verification-status');
          emailVerified = verificationRes.data?.emailVerified === true;
        } catch (verificationError) {
          console.warn('Could not refresh Auth0 email verification status:', verificationError);
        }

        if (!emailVerified) {
          navigate(`/confirmation?email=${encodeURIComponent(email)}`, { replace: true });
          return;
        }

        let accessStatus = null;
        try {
          const accessRes = await api.get('/subscription/access-status');
          accessStatus = accessRes.data;
        } catch (accessError) {
          console.error('Could not verify subscription access:', accessError);
          if (user.role === 'Admin') navigate('/subscription', { replace: true });
          else {
            await Userfront.logout();
            navigate('/login', { replace: true });
          }
          return;
        }

        // Once the trial has expired (and there is no live paid subscription), the
        // application is locked. Admins can only continue to Billing to subscribe.
        if (!accessStatus?.hasAccess) {
          if (user.role === 'Admin') navigate('/subscription', { replace: true });
          else {
            await Userfront.logout();
            navigate('/login', { replace: true });
          }
          return;
        }

        // Team Management is a paid Team-plan capability. A trial or Basic account
        // must not be able to open /team directly even if the URL is known.
        if (location.pathname.startsWith('/team')) {
          const hasPaidTeamAccess =
            user.role === 'Admin' &&
            user.subscriptionType === 'Team' &&
            accessStatus?.hasAccess === true &&
            accessStatus?.trialActive !== true;

          if (!hasPaidTeamAccess) {
            navigate(user.role === 'Admin' ? '/subscription' : '/', { replace: true });
            return;
          }
        }
      } catch (error) {
        console.error('Subscription redirect error:', error);
      }
    };

    checkUserStatus();
  }, [location.pathname, navigate]);

  return null;
};

export default SubscriptionRedirect;
